import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const STORAGE_KEY = "ll_cookie_consent_v1";

type Consent = "all" | "essential";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const hasCookie = document.cookie.split("; ").some((c) => c.startsWith(`${STORAGE_KEY}=`));
      if (!hasCookie && !localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const saveConsent = (choice: Consent) => {
    try {
      document.cookie = `${STORAGE_KEY}=${choice}; max-age=${60 * 60 * 24 * 365}; path=/; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
    } catch {
      /* ignore */
    }
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() }),
      );
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60] rounded-lg border border-border bg-background/95 backdrop-blur shadow-elevated p-4 md:p-5 animate-fade-in"
    >
      <button
        onClick={() => saveConsent("essential")}
        aria-label="Dismiss cookie notice"
        className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X size={14} />
      </button>
      <p className="text-luxury text-xs text-foreground mb-2">Cookies & privacy</p>
      <p className="text-xs text-muted-foreground leading-relaxed pr-4">
        We use strictly necessary cookies to run the shop (cart, session, security). With your
        consent, we may also enable optional cookies to improve your experience. Read our{" "}
        <Link to="/cookies" className="underline hover:text-foreground transition-colors">
          Cookie Policy
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className="underline hover:text-foreground transition-colors">
          Privacy Notice
        </Link>
        .
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => saveConsent("all")}
          className="text-xs px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
        >
          Allow all
        </button>
        <button
          onClick={() => saveConsent("essential")}
          className="text-xs px-4 py-2 rounded-md border border-border text-foreground hover:bg-secondary transition-colors"
        >
          Essentials only
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
